/**
 * Evaluates a parsed query against loaded rows.
 *
 * Semantics chosen to match what people expect from search bars rather than what
 * is easiest to implement:
 *
 *   * Clauses combine with AND. Adding a term narrows.
 *   * `-term` excludes.
 *   * Free text matches across title, artist, album, genre and sub-genre.
 *   * A clause against a value that has not been computed yet does NOT match.
 *     Pending is not zero. Filtering `energy:>0.7` must not silently include
 *     tracks whose energy is still being analysed, nor exclude them from a
 *     negated query on a technicality -- so pending rows drop out of any query
 *     that constrains a pending field, and the UI reports how many.
 */

import { bpmMatchTolerance, decimalsTyped, snapNumber } from "../format/round";
import type { Clause, Query } from "./parse";
import type { Track } from "../types";

/** Value lookup. Returns `undefined` when the value is not yet computed. */
function fieldValue(track: Track, field: string): unknown {
  if (field.startsWith("custom:")) {
    const v = track.custom?.[field.slice("custom:".length)];
    return v === null || v === undefined ? undefined : v;
  }
  switch (field) {
    case "title": return track.title;
    case "artist": return track.artist;
    case "album": return track.album ?? undefined;
    case "year": return track.year ?? undefined;
    case "genre": return track.genre ?? undefined;
    case "subgenre": return track.subGenre ?? undefined;
    case "comment": return track.comment ?? undefined;
    case "path": return track.path;
    case "bpm": return track.bpm ?? undefined;
    case "key": return track.musicalKey ?? undefined;
    case "energy": return track.energy ?? undefined;
    case "danceability": return track.danceability ?? undefined;
    case "group": return track.trackGroups?.length ? track.trackGroups.join(" | ") : undefined;
    case "energylevel": return track.energyLevel ?? undefined;
    case "lufs": return track.lufs ?? undefined;
    case "lra": return track.lra ?? undefined;
    case "truepeak": return track.truePeak ?? undefined;
    case "cuein": return track.cueIn ?? undefined;
    case "cueout": return track.cueOut ?? undefined;
    case "measured": return track.lufs !== null;
    case "archived": return Boolean(track.archivedAt);
    case "missing": return track.missing === true;
    case "needsedit": return Boolean(track.needsEdit);
    case "duration": return track.duration;
    case "intro": return track.introEnd ?? undefined;
    case "tonality": return track.tonality;
    case "confidence": return confidenceBand(track);
    case "analysed": return track.bpm !== null && track.musicalKey !== null;
    case "reviewed": return track.keyConfidence !== null && track.keyConfidence >= 0.85;
    default: return undefined;
  }
}

function confidenceBand(track: Track): string | undefined {
  const c = track.keyConfidence;
  if (c === null) return undefined;
  if (c >= 0.85) return "high";
  if (c >= 0.6) return "medium";
  return "low";
}

function matchesClause(track: Track, clause: Clause): boolean {
  const value = fieldValue(track, clause.field);

  // Not yet analysed: cannot satisfy a constraint on this field either way.
  if (value === undefined) return false;

  let hit: boolean;

  switch (clause.cmp) {
    case "range":
      hit = typeof value === "number" &&
            value >= (clause.value as number) &&
            value <= (clause.value2 as number);
      break;
    case "gt":  hit = typeof value === "number" && value >  (clause.value as number); break;
    case "gte": hit = typeof value === "number" && value >= (clause.value as number); break;
    case "lt":  hit = typeof value === "number" && value <  (clause.value as number); break;
    case "lte": hit = typeof value === "number" && value <= (clause.value as number); break;
    case "prefix": {
      const s = String(value).toLowerCase();
      const p = String(clause.value).toLowerCase();
      hit = s === p || s.startsWith(p);
      break;
    }
    case "contains":
      hit = String(value).toLowerCase().includes(String(clause.value).toLowerCase());
      break;
    case "anyOf": {
      if (Array.isArray((clause.value as unknown[])[0])) {
        // Number ranges: "the 2010s or the 2020s".
        hit = typeof value === "number"
          && (clause.value as unknown as [number, number][]).some(([lo, hi]) => value >= lo && value <= hi);
        break;
      }
      const haystack = String(value).toLowerCase();
      hit = (clause.value as string[]).some((option) => haystack.includes(option.toLowerCase()));
      break;
    }
    case "eq":
    default:
      if (typeof value === "boolean" || typeof clause.value === "boolean") {
        hit = value === clause.value;
      } else if (clause.field === "bpm" && typeof value === "number") {
        // Match what the grid SHOWS, not the raw stored figure.
        //
        // A track stored as 101.981 displays as 102 under the rounding rule, so
        // searching bpm:102 found nothing at all -- the number on screen was not
        // a number you could search for. A whole-number query matches anything
        // that displays as it; a decimal query matches to the precision typed,
        // because the grid shows one decimal and a stored 100.724 appears there
        // as "100.7" -- searching the number on screen has to find it.
        const target = Number(clause.value);
        hit = Number.isInteger(target)
          ? snapNumber(value) === target
          : Math.abs(value - target) < bpmMatchTolerance(decimalsTyped(clause.value as number));
      } else {
        hit = String(value).toLowerCase() === String(clause.value).toLowerCase();
      }
      break;
  }

  return clause.negated ? !hit : hit;
}

// Comment is included because stations use it as an ad-hoc field store, so a
// bare word a curator half-remembers is as likely to be in a comment as a title.
const FREE_TEXT_FIELDS = [
  "title", "artist", "album", "genre", "subgenre", "comment",
] as const;

function matchesFree(track: Track, text: string): boolean {
  const q = text.toLowerCase();
  return FREE_TEXT_FIELDS.some((f) => {
    const v = fieldValue(track, f);
    return v !== undefined && String(v).toLowerCase().includes(q);
  });
}

export interface EvalResult {
  matched: Track[];
  /** Rows excluded only because a constrained field is still being analysed. */
  pendingExcluded: number;
}

export function evaluate(tracks: Track[], query: Query): EvalResult {
  if (query.isEmpty) return { matched: tracks, pendingExcluded: 0 };

  const matched: Track[] = [];
  let pendingExcluded = 0;

  for (const track of tracks) {
    const clausesOk = query.clauses.every((c) => matchesClause(track, c));
    const freeOk = query.free.every((f) => {
      const hit = matchesFree(track, f.text);
      return f.negated ? !hit : hit;
    });

    if (clausesOk && freeOk) {
      matched.push(track);
      continue;
    }

    // Distinguish "genuinely does not match" from "cannot know yet".
    const blockedByPending = query.clauses.some(
      (c) => !c.negated && fieldValue(track, c.field) === undefined
    );
    if (blockedByPending) pendingExcluded++;
  }

  return { matched, pendingExcluded };
}
